import React, { useContext, useEffect } from "react";
import classNames from "classnames";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { PaymentOption } from "@api-types/withdrawal";
import { WithdrawalContext } from "@state/context/payment/WithdrawalContext";
import InfoMessageComponent from "@components-shared/InfoMessageComponent";
import PaymentOptionImage from "@components/icons/PaymentOptionImage";
import { getSVGExtension } from "@utils/common/helpers";

export default observer(function WithdrawalPaymentSelect() {
	const {
		paymentMethods,
		fetchPaymentMethods,
		isFetchingPaymentMethods,
		hasWalletChangedFromDefault,
		changeWalletFromDefault,
		paymentMethodError,
	} = useContext(WithdrawalContext);
	const { t } = useTranslation("payment");
	const { watch } = useFormContext();

	const bettingAccountTypeId = watch("bettingAccountTypeId");

	useEffect(() => {
		//refetch payment methods when wallet changes
		if (bettingAccountTypeId && hasWalletChangedFromDefault) {
			fetchPaymentMethods(bettingAccountTypeId);
		} else {
			changeWalletFromDefault();
		}
	}, [bettingAccountTypeId]);

	if (isFetchingPaymentMethods === true) {
		//skeleton loader?
		return null;
	}

	if (paymentMethodError?.errorCode === 8) {
		return (
			<InfoMessageComponent
				type="warning"
				message={paymentMethodError.message}
				textClass="message__text"
			/>
		);
	}

	if (paymentMethods == null || paymentMethods.length == 0) {
		//return view of no payment methods
		return (
			<>
				<div className="label">{t("COMMON.USER_PAYMENT_METHODS")}</div>
				<InfoMessageComponent
					type="note"
					message={t("COMMON.NO_PAYMENT_OPTIONS")}
					textClass="message__text"
				/>
			</>
		);
	}

	return <PaymentMethodList />;
});

export const PaymentMethodList = observer(function PaymentMethodList() {
	const { otherPaymentMethods, userPaymentSavedMethods } =
		useContext(WithdrawalContext);
	const { t } = useTranslation("payment");
	const { resetField } = useFormContext();
	useEffect(() => {
		resetField("paymentMethodId", {
			defaultValue:
				userPaymentSavedMethods?.length != 0
					? userPaymentSavedMethods &&
						userPaymentSavedMethods[0]?.paymentMethodId
					: otherPaymentMethods &&
						otherPaymentMethods[0]?.paymentMethodId,
		});
	}, []);

	return (
		<>
			{userPaymentSavedMethods?.length != 0 && (
				<div className="form__group">
					<div
						className={classNames("label", {
							"u-mar--bottom--xsml":
								userPaymentSavedMethods &&
								userPaymentSavedMethods?.length > 2,
						})}
					>
						{t("COMMON.USER_PAYMENT_METHODS")}
					</div>

					<div className="row">
						{userPaymentSavedMethods?.map(
							(userPaymentMethod: PaymentOption) => {
								return (
									<div
										key={userPaymentMethod.paymentMethodId}
										className="col col-sml-12 col-med-12 col-lrg-12 col-xlrg-12 col-ssvga-12 col-hdlow-12 col-xxlrg-12 col-huge-12 col-xxxlrg-6"
									>
										<PaymentMethodOption
											paymentMethod={userPaymentMethod}
										/>
									</div>
								);
							}
						)}
					</div>
				</div>
			)}

			{otherPaymentMethods?.length != 0 && (
				<div className="form__group">
					<div
						className={classNames("label", {
							"u-mar--bottom--xsml":
								otherPaymentMethods &&
								otherPaymentMethods?.length > 2,
						})}
					>
						{t("COMMON.OTHER_PAYMENT_METHODS")}
					</div>
					{otherPaymentMethods?.map(
						(paymentMethod: PaymentOption) => {
							return (
								<PaymentMethodOption
									key={paymentMethod.paymentMethodId}
									paymentMethod={paymentMethod}
								/>
							);
						}
					)}
				</div>
			)}
		</>
	);
});

const PaymentMethodOption = observer(function PaymentMethodOption(props: {
	paymentMethod: PaymentOption;
}) {
	const { paymentMethod } = props;

	const { register } = useFormContext();

	const { t } = useTranslation("payment");

	const providerLogoClasses = classNames(
		"card--payment__logo u-icon u-icon--payment u-icon--payment--med",
		[`u-icon--payment--${paymentMethod.paymentMethodAbrv.toLowerCase()}`]
	);

	return (
		<div key={paymentMethod.paymentMethodId} className="card--payment">
			<div className="card--payment__area input--radio input--radio--sml">
				<input
					type="radio"
					id={`${paymentMethod.paymentMethodId}`}
					value={paymentMethod.paymentMethodId}
					className="input--radio__box input--radio--sml__box"
					{...register("paymentMethodId")}
					required
				/>
				<label
					htmlFor={`${paymentMethod.paymentMethodId}`}
					className="card--payment__area input--radio__label input--radio--sml__label"
				>
					<span className="card--payment__icon">
						<PaymentOptionImage
							className={providerLogoClasses}
							svgName={
								paymentMethod.paymentMethodAbrv.toLowerCase() +
								getSVGExtension()
							}
						/>
						<span className="card--payment__card">
							{paymentMethod.maskedPan}
						</span>
					</span>
					<span className="card--payment__details">
						<span className="card--payment__title">
							{paymentMethod.displayName}
						</span>
						<span className="card--payment__row">
							<span className="card--payment__col">
								{t<string>("COMMON.MIN_AMOUNT")}:
							</span>
							<span className="card--payment__col">
								{paymentMethod.minPayout}
								{paymentMethod.currencyDisplayCode}
							</span>
						</span>
						<span className="card--payment__row">
							<span className="card--payment__col">
								{t<string>("COMMON.MAX_AMOUNT")}:
							</span>
							<span className="card--payment__col">
								{paymentMethod.maxPayout}
								{paymentMethod.currencyDisplayCode}
							</span>
						</span>
					</span>
				</label>
			</div>
		</div>
	);
});
